"use client";

import { useEffect, useRef, useState } from "react";
import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import {
  Show,
  SignInButton,
  SignUpButton,
  UserButton,
} from "@clerk/nextjs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { ModeToggle } from "@/components/mode-toggle";
import { Search, ShoppingCart, User, Menu, X } from "lucide-react";
import { useAppState } from "@/lib/app-state";
import {
  dictionary,
  getLocaleFromPathname,
  hrefWithLocale,
} from "@/lib/i18n";

interface HeaderProps {
  isLoggedIn?: boolean;
  cartItemCount?: number;
  onLogin?: () => void;
  onLogout?: () => void;
}

export function Header({
  isLoggedIn = false,
  cartItemCount = 0,
  onLogin,
  onLogout,
}: HeaderProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  const profileMenuRef = useRef<HTMLDivElement | null>(null);
  const router = useRouter();
  const pathname = usePathname();
  const locale = getLocaleFromPathname(pathname);
  const t = dictionary[locale].header;
  const appState = useAppState();
  const switchLocale = (nextLocale: "ko" | "en") => {
    const segments = pathname.split("/").filter(Boolean);
    const normalizedPath =
      segments.length > 0 && (segments[0] === "ko" || segments[0] === "en")
        ? `/${segments.slice(1).join("/")}`
        : pathname;
    const targetPath =
      normalizedPath === "/"
        ? `/${nextLocale}`
        : `/${nextLocale}${normalizedPath}`;
    router.push(targetPath);
  };

  const legacyMode = Boolean(onLogin || onLogout);
  const effectiveIsLoggedIn = legacyMode ? isLoggedIn : appState.isLoggedIn;
  const effectiveCartItemCount = legacyMode ? cartItemCount : appState.cartItemCount;
  const handleLogin = onLogin ?? appState.login;
  const handleLogout = onLogout ?? appState.logout;

  useEffect(() => {
    if (!legacyMode || !isProfileMenuOpen) return;

    const handleOutsideClick = (event: MouseEvent) => {
      if (!profileMenuRef.current) return;
      if (!profileMenuRef.current.contains(event.target as Node)) {
        setIsProfileMenuOpen(false);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsProfileMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [legacyMode, isProfileMenuOpen]);

  useEffect(() => {
    if (!isMobileMenuOpen) return;

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsMobileMenuOpen(false);
      }
    };

    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isMobileMenuOpen]);

  return (
    <header className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="container mx-auto px-4">
        <div className="flex h-16 items-center justify-between">
          {/* Logo */}
          <Link
            href={hrefWithLocale(locale, "/")}
            className="flex items-center space-x-2"
          >
            <span className="text-xl font-bold text-foreground">
              PromptMarket
            </span>
          </Link>

          <div className="hidden md:flex flex-1 max-w-md mx-8">
            <div className="relative w-full">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder={t.searchPlaceholder}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>

          <nav className="hidden md:flex items-center space-x-6">
            <Link
              href={hrefWithLocale(locale, "/prompts")}
              className="text-muted-foreground transition-colors hover:text-foreground"
            >
              {t.prompts}
            </Link>

            <ModeToggle />
            <div className="inline-flex items-center rounded-md border p-1 text-xs">
              <button
                type="button"
                onClick={() => switchLocale("ko")}
                className={`rounded px-2 py-1 ${locale === "ko" ? "bg-accent text-accent-foreground" : "text-muted-foreground"}`}
              >
                KO
              </button>
              <button
                type="button"
                onClick={() => switchLocale("en")}
                className={`rounded px-2 py-1 ${locale === "en" ? "bg-accent text-accent-foreground" : "text-muted-foreground"}`}
              >
                EN
              </button>
            </div>

            {legacyMode ? (
              effectiveIsLoggedIn ? (
                <>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="relative text-muted-foreground hover:text-foreground"
                    asChild
                  >
                    <Link href={hrefWithLocale(locale, "/cart")}>
                      <ShoppingCart className="h-5 w-5" />
                      {effectiveCartItemCount > 0 && (
                        <Badge
                          variant="destructive"
                          className="absolute -top-2 -right-2 h-5 w-5 rounded-full p-0 flex items-center justify-center text-xs"
                        >
                          {effectiveCartItemCount}
                        </Badge>
                      )}
                    </Link>
                  </Button>

                  <div className="relative" ref={profileMenuRef}>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="text-muted-foreground hover:text-foreground"
                      aria-haspopup="menu"
                      aria-expanded={isProfileMenuOpen}
                      onClick={() => setIsProfileMenuOpen((prev) => !prev)}
                    >
                      <User className="h-5 w-5" />
                    </Button>

                    {isProfileMenuOpen && (
                      <div className="absolute right-0 top-12 z-[60] w-56 overflow-hidden rounded-md border bg-popover text-popover-foreground shadow-lg">
                        <Link
                          href={hrefWithLocale(locale, "/profile")}
                          className="block px-3 py-2 text-sm hover:bg-accent hover:text-accent-foreground"
                          onClick={() => setIsProfileMenuOpen(false)}
                        >
                          {t.profile}
                        </Link>
                        <Link
                          href={hrefWithLocale(locale, "/admin/prompts")}
                          className="block px-3 py-2 text-sm hover:bg-accent hover:text-accent-foreground"
                          onClick={() => setIsProfileMenuOpen(false)}
                        >
                          {t.managePrompts}
                        </Link>
                        <Link
                          href={hrefWithLocale(locale, "/purchase-history")}
                          className="block px-3 py-2 text-sm hover:bg-accent hover:text-accent-foreground"
                          onClick={() => setIsProfileMenuOpen(false)}
                        >
                          {t.purchaseHistory}
                        </Link>
                        <button
                          type="button"
                          onClick={() => {
                            handleLogout();
                            setIsProfileMenuOpen(false);
                          }}
                          className="block w-full border-t px-3 py-2 text-left text-sm hover:bg-accent hover:text-accent-foreground"
                        >
                          {t.logout}
                        </button>
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <>
                  <Button
                    variant="ghost"
                    onClick={handleLogin}
                    className="text-muted-foreground hover:text-foreground"
                  >
                    {t.login}
                  </Button>
                  <Button
                    onClick={handleLogin}
                    className="bg-foreground text-background hover:bg-foreground/90"
                  >
                    {t.signUp}
                  </Button>
                </>
              )
            ) : (
              <>
                <Show when="signed-in">
                  <>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="relative text-muted-foreground hover:text-foreground"
                      asChild
                    >
                      <Link href={hrefWithLocale(locale, "/cart")}>
                        <ShoppingCart className="h-5 w-5" />
                        {effectiveCartItemCount > 0 && (
                          <Badge
                            variant="destructive"
                            className="absolute -top-2 -right-2 h-5 w-5 rounded-full p-0 flex items-center justify-center text-xs"
                          >
                            {effectiveCartItemCount}
                          </Badge>
                        )}
                      </Link>
                    </Button>
                    <Link
                      href={hrefWithLocale(locale, "/profile")}
                      className="text-sm text-muted-foreground transition-colors hover:text-foreground"
                    >
                      {t.profile}
                    </Link>
                    <Link
                      href={hrefWithLocale(locale, "/admin/prompts")}
                      className="text-sm text-muted-foreground transition-colors hover:text-foreground"
                    >
                      {t.managePrompts}
                    </Link>
                    <Link
                      href={hrefWithLocale(locale, "/purchase-history")}
                      className="text-sm text-muted-foreground transition-colors hover:text-foreground"
                    >
                      {t.purchaseHistory}
                    </Link>
                    <UserButton />
                  </>
                </Show>
                <Show when="signed-out">
                  <>
                    <SignInButton mode="modal">
                      <Button
                        variant="ghost"
                        className="text-muted-foreground hover:text-foreground"
                      >
                        {t.login}
                      </Button>
                    </SignInButton>
                    <SignUpButton mode="modal">
                      <Button className="bg-foreground text-background hover:bg-foreground/90">
                        {t.signUp}
                      </Button>
                    </SignUpButton>
                  </>
                </Show>
              </>
            )}
          </nav>

          <div className="flex md:hidden items-center space-x-2">
            <ModeToggle />
            <div className="inline-flex items-center rounded-md border p-1 text-xs">
              <button
                type="button"
                onClick={() => switchLocale("ko")}
                className={`rounded px-2 py-1 ${locale === "ko" ? "bg-accent text-accent-foreground" : "text-muted-foreground"}`}
              >
                KO
              </button>
              <button
                type="button"
                onClick={() => switchLocale("en")}
                className={`rounded px-2 py-1 ${locale === "en" ? "bg-accent text-accent-foreground" : "text-muted-foreground"}`}
              >
                EN
              </button>
            </div>
            {legacyMode ? (
              effectiveIsLoggedIn && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="relative text-muted-foreground hover:text-foreground"
                  asChild
                >
                  <Link href={hrefWithLocale(locale, "/cart")}>
                    <ShoppingCart className="h-5 w-5" />
                    {effectiveCartItemCount > 0 && (
                      <Badge
                        variant="destructive"
                        className="absolute -top-2 -right-2 h-5 w-5 rounded-full p-0 flex items-center justify-center text-xs"
                      >
                        {effectiveCartItemCount}
                      </Badge>
                    )}
                  </Link>
                </Button>
              )
            ) : (
              <Show when="signed-in">
                <Button
                  variant="ghost"
                  size="icon"
                  className="relative text-muted-foreground hover:text-foreground"
                  asChild
                >
                  <Link href={hrefWithLocale(locale, "/cart")}>
                    <ShoppingCart className="h-5 w-5" />
                    {effectiveCartItemCount > 0 && (
                      <Badge
                        variant="destructive"
                        className="absolute -top-2 -right-2 h-5 w-5 rounded-full p-0 flex items-center justify-center text-xs"
                      >
                        {effectiveCartItemCount}
                      </Badge>
                    )}
                  </Link>
                </Button>
              </Show>
            )}
            <Button
              variant="ghost"
              size="icon"
              className="text-muted-foreground hover:text-foreground"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            >
              {isMobileMenuOpen ? (
                <X className="h-5 w-5" />
              ) : (
                <Menu className="h-5 w-5" />
              )}
            </Button>
          </div>
        </div>

        {isMobileMenuOpen && (
          <div className="md:hidden border-t py-4">
            {/* Mobile Search */}
            <div className="mb-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder={t.searchPlaceholder}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>

            {/* Mobile Navigation */}
            <div className="space-y-2">
              <Link
                href={hrefWithLocale(locale, "/prompts")}
                className="block rounded-md px-3 py-2 text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                {t.prompts}
              </Link>

              {legacyMode ? (
                effectiveIsLoggedIn ? (
                  <>
                    <Link
                      href={hrefWithLocale(locale, "/profile")}
                      className="block rounded-md px-3 py-2 text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      {t.profile}
                    </Link>
                    <Link
                      href={hrefWithLocale(locale, "/admin/prompts")}
                      className="block rounded-md px-3 py-2 text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      {t.managePrompts}
                    </Link>
                    <Link
                      href={hrefWithLocale(locale, "/purchase-history")}
                      className="block rounded-md px-3 py-2 text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      {t.purchaseHistory}
                    </Link>
                    <button
                      onClick={() => {
                        handleLogout();
                        setIsMobileMenuOpen(false);
                      }}
                      className="block w-full rounded-md px-3 py-2 text-left text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
                    >
                      {t.logout}
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      onClick={() => {
                        handleLogin();
                        setIsMobileMenuOpen(false);
                      }}
                      className="block w-full rounded-md px-3 py-2 text-left text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
                    >
                      {t.login}
                    </button>
                    <button
                      onClick={() => {
                        handleLogin();
                        setIsMobileMenuOpen(false);
                      }}
                      className="block w-full rounded-md bg-foreground px-3 py-2 text-left text-background transition-colors hover:bg-foreground/90"
                    >
                      {t.signUp}
                    </button>
                  </>
                )
              ) : (
                <>
                  <Show when="signed-in">
                    <>
                      <Link
                        href={hrefWithLocale(locale, "/profile")}
                        className="block rounded-md px-3 py-2 text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
                        onClick={() => setIsMobileMenuOpen(false)}
                      >
                        {t.profile}
                      </Link>
                      <Link
                        href={hrefWithLocale(locale, "/admin/prompts")}
                        className="block rounded-md px-3 py-2 text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
                        onClick={() => setIsMobileMenuOpen(false)}
                      >
                        {t.managePrompts}
                      </Link>
                      <Link
                        href={hrefWithLocale(locale, "/purchase-history")}
                        className="block rounded-md px-3 py-2 text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
                        onClick={() => setIsMobileMenuOpen(false)}
                      >
                        {t.purchaseHistory}
                      </Link>
                      <div className="px-3 py-2">
                        <UserButton />
                      </div>
                    </>
                  </Show>
                  <Show when="signed-out">
                    <>
                      <div className="px-3 py-2">
                        <SignInButton mode="modal">
                          <button
                            type="button"
                            className="block w-full rounded-md px-3 py-2 text-left text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
                          >
                            {t.login}
                          </button>
                        </SignInButton>
                      </div>
                      <div className="px-3 py-2">
                        <SignUpButton mode="modal">
                          <button
                            type="button"
                            className="block w-full rounded-md bg-foreground px-3 py-2 text-left text-background transition-colors hover:bg-foreground/90"
                          >
                            {t.signUp}
                          </button>
                        </SignUpButton>
                      </div>
                    </>
                  </Show>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </header>
  );
}
